import { useState } from "react";
import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import { Trash2 } from "lucide-react";

import { Button, buttonVariants } from "components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from "components/ui/combobox";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from "components/ui/item";
import { buildQueryParams } from "components/NewAdvancedSearch/buildQueryParams";
import {
  useCreateSavedFilter,
  useDeleteSavedFilter,
  useSaveFilters,
} from "components/NewAdvancedSearch/SaveFilters/useSaveFilters";
import { DeleteSavedFilterDialog } from "./DeleteSavedFilterDialog";
import { SaveFilterDialog } from "./SaveFilterDialog";

export function SaveFiltersSelect({ tableName, rules = [], onSelect }) {
  const { data } = useSaveFilters({ tableName });
  const { mutate: deleteFilter } = useDeleteSavedFilter({ tableName });
  const { mutate: createFilter, isPending: isSaving } = useCreateSavedFilter({
    tableName,
  });
  const [pendingDelete, setPendingDelete] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);

  return (
    <>
      <Combobox
        items={data}
        selectionMode="single"
        onValueChange={value => {
          value && onSelect?.(value);
        }}
      >
        <ComboboxTrigger
          className={buttonVariants({
            variant: "outline",
            className:
              "tw:w-44 tw:max-w-full tw:justify-between tw:font-normal ",
          })}
        >
          <ComboboxValue>
            {selected =>
              selected ? (
                <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-left">
                  {selected.name}
                </span>
              ) : (
                <span className="tw:text-muted-foreground">Saved Filters</span>
              )
            }
          </ComboboxValue>
        </ComboboxTrigger>
        <ComboboxContent>
          <ComboboxInput showTrigger={false} placeholder="Search" />
          <ComboboxEmpty>No items found.</ComboboxEmpty>
          <ComboboxList>
            {item => (
              <ComboboxPrimitive.Item
                key={`saved-filter-${item.name}`}
                value={item}
                data-slot="combobox-item"
                className="tw:relative tw:flex tw:w-full tw:cursor-default tw:items-center tw:gap-2 tw:rounded-md tw:py-1 t tw:px-1.5 tw:text-sm tw:outline-hidden tw:select-none tw:data-highlighted:bg-accent tw:data-highlighted:text-accent-foreground tw:not-data-[variant=destructive]:data-highlighted:**:text-accent-foreground tw:data-disabled:pointer-events-none tw:data-disabled:opacity-50 tw:[&_svg]:pointer-events-none tw:[&_svg]:shrink-0 tw:[&_svg:not([class*=size-])]:size-4"
              >
                <Item size="xs" className="tw:p-0">
                  <ItemContent>
                    <ItemTitle className="whitespace-nowrap">
                      {item.name}
                    </ItemTitle>
                    <ItemDescription>{item.description}</ItemDescription>
                  </ItemContent>
                  <ItemActions>
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon-xs"
                      onClick={e => {
                        // Stop the click from bubbling to the combobox item,
                        // which would select the row and close the popup.
                        e.stopPropagation();
                        setPendingDelete(item);
                      }}
                    >
                      <Trash2 />
                    </Button>
                  </ItemActions>
                </Item>
              </ComboboxPrimitive.Item>
            )}
          </ComboboxList>
          <div className="tw:w-full tw:flex tw:justify-center tw:items-center tw:pb-1">
            <Button type="button" onClick={() => setSaveOpen(true)}>
              Save new filter
            </Button>
          </div>
        </ComboboxContent>
      </Combobox>
      <DeleteSavedFilterDialog
        filter={pendingDelete}
        open={!!pendingDelete}
        onOpenChange={open => !open && setPendingDelete(null)}
        onConfirm={filter => {
          deleteFilter(filter);
          setPendingDelete(null);
        }}
      />
      <SaveFilterDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        isSaving={isSaving}
        onSave={({ name, description }) =>
          createFilter(
            { name, description, state: buildQueryParams(rules) },
            { onSuccess: () => setSaveOpen(false) }
          )
        }
      />
    </>
  );
}
